import {
  ApiBlueprint,
  PageBlueprint,
  SubPageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  createRouteRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';

import { CowboyHatIcon } from './components/CowboyHatIcon';
import { lassoApiRef } from './api/LassoApi';
import { LassoClient } from './api/LassoClient';

export const rootRouteRef = createRouteRef();
export const buildsRouteRef = createRouteRef();
export const testHistoryRouteRef = createRouteRef();
export const flakyTestsRouteRef = createRouteRef();

const lassoApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: lassoApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new LassoClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// The parent page has no loader of its own — the sub-pages below attach to
// its `pages` input and render as tabs, with `/lasso` redirecting to the
// first tab. The plugin-level title/icon make the app derive a single
// "Lasso" sidebar item for it.
const lassoPage = PageBlueprint.make({
  params: {
    path: '/lasso',
    routeRef: rootRouteRef,
  },
});

const lassoBuildsPage = SubPageBlueprint.make({
  name: 'builds',
  params: {
    path: 'builds',
    title: 'Builds',
    routeRef: buildsRouteRef,
    loader: () =>
      import('./components/LassoBuildsPage').then(m =>
        compatWrapper(<m.LassoBuildsPage />),
      ),
  },
});

const lassoTestHistoryPage = SubPageBlueprint.make({
  name: 'tests',
  params: {
    path: 'tests',
    title: 'Test History',
    routeRef: testHistoryRouteRef,
    loader: () =>
      import('./components/LassoTestHistoryPage').then(m =>
        compatWrapper(<m.LassoTestHistoryPage />),
      ),
  },
});

const lassoFlakyTestsPage = SubPageBlueprint.make({
  name: 'flakes',
  params: {
    path: 'flakes',
    title: 'Flaky Tests',
    routeRef: flakyTestsRouteRef,
    loader: () =>
      import('./components/LassoFlakyTestsPage').then(m =>
        compatWrapper(<m.LassoFlakyTestsPage />),
      ),
  },
});

// The trace viewer is only reachable via links from the builds/test-history
// views, so it stays a standalone page. It intentionally declares no
// `routeRef`, which keeps the app from deriving a sidebar item for it.
const lassoAttemptsPage = PageBlueprint.make({
  name: 'attempts',
  params: {
    path: '/lasso/attempts',
    loader: () =>
      import('./components/LassoAttemptsPage').then(m =>
        compatWrapper(<m.LassoAttemptsPage />),
      ),
  },
});

/**
 * The Backstage new-frontend-system version of the lasso plugin.
 *
 * @public
 */
export default createFrontendPlugin({
  pluginId: 'lasso',
  title: 'Lasso',
  icon: <CowboyHatIcon />,
  routes: {
    root: rootRouteRef,
    builds: buildsRouteRef,
    tests: testHistoryRouteRef,
    flakes: flakyTestsRouteRef,
  },
  extensions: [
    lassoApi,
    lassoPage,
    lassoBuildsPage,
    lassoTestHistoryPage,
    lassoFlakyTestsPage,
    lassoAttemptsPage,
  ],
});
